/**
 * Field Validation Service
 * Centralized validation logic for form fields across the application
 * REG-006: Extract Validation Logic
 */

import { validatePasswordStrength, getPasswordErrorMessage } from '@/utils/password-validation';
import { PasswordValidationResult } from '@/types/password-validation.types';
import { checkCompanyNameExists } from './organization.service';

// ============= TYPE DEFINITIONS =============

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

export interface AsyncValidationResult extends ValidationResult {
  isChecking?: boolean;
}

export interface PasswordValidation extends ValidationResult {
  strength?: 'weak' | 'fair' | 'good' | 'strong';
  score?: number;
  requirements?: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
    notCommon: boolean;
  };
}

export interface EmailValidationConfig {
  required?: boolean;
  allowSubdomains?: boolean;
  blockedDomains?: string[];
}

export interface CompanyNameValidationConfig {
  minLength?: number;
  maxLength?: number;
  allowSpecialChars?: boolean;
  checkDuplicates?: boolean;
}

// ============= EMAIL VALIDATION =============

/**
 * Validates an email address
 * @param email - The email to validate
 * @param config - Optional configuration for email validation
 * @returns ValidationResult with error message if invalid
 */
export function validateEmail(
  email: string,
  config: EmailValidationConfig = {}
): ValidationResult {
  const { required = true, blockedDomains = [] } = config;

  // Required check
  if (required && !email) {
    return {
      isValid: false,
      error: 'Email is required'
    };
  }

  // If not required and empty, it's valid
  if (!required && !email) {
    return { isValid: true };
  }

  // Email format validation (RFC 5322 simplified)
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    };
  }

  // Check blocked domains
  const domain = email.split('@')[1]?.toLowerCase();
  if (blockedDomains.includes(domain)) {
    return {
      isValid: false,
      error: 'This email domain is not allowed'
    };
  }

  return { isValid: true };
}

// ============= NAME VALIDATION =============

/**
 * Validates a person's name (first name or last name)
 * @param name - The name to validate
 * @param fieldName - Display name for error messages (e.g., "First name", "Last name")
 * @param required - Whether the field is required (default: true)
 * @returns ValidationResult with error message if invalid
 */
export function validateName(
  name: string,
  fieldName: string = 'Name',
  required: boolean = true
): ValidationResult {
  // Required check
  if (required && !name) {
    return {
      isValid: false,
      error: `${fieldName} is required`
    };
  }

  // If not required and empty, it's valid
  if (!required && !name) {
    return { isValid: true };
  }

  // Minimum length (2 characters)
  if (name.length < 2) {
    return {
      isValid: false,
      error: `${fieldName} must be at least 2 characters`
    };
  }

  // Pattern validation (letters, spaces, hyphens, apostrophes only)
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  if (!nameRegex.test(name)) {
    return {
      isValid: false,
      error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`
    };
  }

  return { isValid: true };
}

// ============= PASSWORD VALIDATION =============

/**
 * Validates a password using the password strength utility
 * @param password - The password to validate
 * @returns PasswordValidation result with strength and requirements
 */
export function validatePassword(password: string): PasswordValidation {
  if (!password) {
    return {
      isValid: false,
      error: 'Password is required'
    };
  }

  const validation = validatePasswordStrength(password);
  
  return {
    isValid: validation.isValid,
    error: validation.isValid ? undefined : getPasswordErrorMessage(validation),
    strength: validation.strength,
    requirements: validation.requirements
  };
}

/**
 * Validates password confirmation matches the original password
 * @param password - The original password
 * @param confirmPassword - The confirmation password
 * @returns ValidationResult with error if passwords don't match
 */
export function validatePasswordConfirmation(
  password: string,
  confirmPassword: string
): ValidationResult {
  if (!confirmPassword) {
    return {
      isValid: false,
      error: 'Please confirm your password'
    };
  }

  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: "Passwords don't match"
    };
  }

  return { isValid: true };
}

// ============= COMPANY NAME VALIDATION =============

/**
 * Validates a company name (synchronous)
 * @param name - The company name to validate
 * @param config - Optional configuration
 * @returns ValidationResult with error if invalid
 */
export function validateCompanyName(
  name: string,
  config: CompanyNameValidationConfig = {}
): ValidationResult {
  const { minLength = 3, maxLength = 100 } = config;

  if (!name || name.trim().length === 0) {
    return {
      isValid: false,
      error: 'Company name is required'
    };
  }

  const trimmedName = name.trim();

  if (trimmedName.length < minLength) {
    return {
      isValid: false,
      error: `Company name must be at least ${minLength} characters`
    };
  }

  if (trimmedName.length > maxLength) {
    return {
      isValid: false,
      error: `Company name must be less than ${maxLength} characters`
    };
  }

  return { isValid: true };
}

/**
 * Validates a company name including duplicate check (asynchronous)
 * @param name - The company name to validate
 * @returns Promise<AsyncValidationResult> with error if invalid or duplicate
 */
export async function validateCompanyNameAsync(
  name: string
): Promise<AsyncValidationResult> {
  // First, run synchronous validation
  const syncValidation = validateCompanyName(name);
  if (!syncValidation.isValid) {
    return syncValidation;
  }

  // Check for duplicates
  try {
    const result = await checkCompanyNameExists(name);
    
    if (result.exists) {
      return {
        isValid: false,
        error: `A company with the name "${result.organization?.name}" already exists in our system.`
      };
    }

    return { isValid: true };
  } catch (error) {
    // Return valid if check fails (graceful degradation)
    return {
      isValid: true,
      warnings: ['Unable to verify company name uniqueness. Please continue.']
    };
  }
}

// ============= GENERIC VALIDATORS =============

/**
 * Validates a required field
 * @param value - The value to check
 * @param fieldName - Display name for error messages
 * @returns ValidationResult with error if empty
 */
export function validateRequired(value: any, fieldName: string): ValidationResult {
  if (value === null || value === undefined || value === '' || 
      (typeof value === 'string' && value.trim() === '')) {
    return {
      isValid: false,
      error: `${fieldName} is required`
    };
  }

  // Check for empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return {
      isValid: false,
      error: `At least one ${fieldName} is required`
    };
  }

  return { isValid: true };
}

/**
 * Validates minimum length
 * @param value - The string to validate
 * @param minLength - Minimum required length
 * @param fieldName - Display name for error messages
 * @returns ValidationResult with error if too short
 */
export function validateMinLength(
  value: string,
  minLength: number,
  fieldName: string
): ValidationResult {
  if (value.length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} characters`
    };
  }
  return { isValid: true };
}

/**
 * Validates maximum length
 * @param value - The string to validate
 * @param maxLength - Maximum allowed length
 * @param fieldName - Display name for error messages
 * @returns ValidationResult with error if too long
 */
export function validateMaxLength(
  value: string,
  maxLength: number,
  fieldName: string
): ValidationResult {
  if (value.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must be less than ${maxLength} characters`
    };
  }
  return { isValid: true };
}

/**
 * Validates value against a regex pattern
 * @param value - The string to validate
 * @param pattern - The regex pattern
 * @param fieldName - Display name for error messages
 * @param message - Custom error message
 * @returns ValidationResult with error if pattern doesn't match
 */
export function validatePattern(
  value: string,
  pattern: RegExp,
  fieldName: string,
  message?: string
): ValidationResult {
  if (!pattern.test(value)) {
    return {
      isValid: false,
      error: message || `${fieldName} format is invalid`
    };
  }
  return { isValid: true };
}

/**
 * Validates a selection field (dropdown, radio, etc.)
 * @param value - The selected value
 * @param fieldName - Display name for error messages
 * @returns ValidationResult with error if not selected
 */
export function validateSelection(value: string, fieldName: string): ValidationResult {
  if (!value || value === '') {
    return {
      isValid: false,
      error: `Please select ${fieldName}`
    };
  }
  return { isValid: true };
}

/**
 * Validates at least one checkbox is selected
 * @param values - Array of selected values
 * @param fieldName - Display name for error messages
 * @returns ValidationResult with error if none selected
 */
export function validateCheckboxGroup(
  values: string[],
  fieldName: string
): ValidationResult {
  if (!values || values.length === 0) {
    return {
      isValid: false,
      error: `Please select at least one ${fieldName}`
    };
  }
  return { isValid: true };
}

// ============= COMPOSITE VALIDATORS =============

/**
 * Validates a complete registration form
 * @param data - Registration form data
 * @returns Record of field names to validation results
 */
export function validateRegistrationForm(data: {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}): Record<string, ValidationResult> {
  return {
    email: validateEmail(data.email),
    password: validatePassword(data.password),
    confirmPassword: validatePasswordConfirmation(data.password, data.confirmPassword),
    firstName: validateName(data.firstName, 'First name'),
    lastName: validateName(data.lastName, 'Last name')
  };
}

/**
 * Validates a complete profile form
 * @param data - Profile form data
 * @returns Record of field names to validation results
 */
export function validateProfileForm(data: {
  role: string;
  company_name?: string;
  business_structure?: string;
  industry?: string;
  goals?: string[];
}): Record<string, ValidationResult> {
  const results: Record<string, ValidationResult> = {
    role: validateRequired(data.role, 'role')
  };

  // Business owner specific validations
  if (data.role === 'business_owner') {
    if (data.company_name !== undefined) {
      results.company_name = validateCompanyName(data.company_name);
    }
    if (data.business_structure !== undefined) {
      results.business_structure = validateSelection(data.business_structure, 'business structure');
    }
    if (data.industry !== undefined) {
      results.industry = validateSelection(data.industry, 'industry');
    }
    if (data.goals !== undefined) {
      results.goals = validateCheckboxGroup(data.goals, 'goal');
    }
  }

  return results;
}

// Export all validators as a namespace for easier imports
export const FieldValidation = {
  validateEmail,
  validateName,
  validatePassword,
  validatePasswordConfirmation,
  validateCompanyName,
  validateCompanyNameAsync,
  validateRequired,
  validateMinLength,
  validateMaxLength,
  validatePattern,
  validateSelection,
  validateCheckboxGroup,
  validateRegistrationForm,
  validateProfileForm
};
